import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { namespace } from '@common-config/constants';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { selectUserObject } from '@common-reducers/UserSelectors';
import { backendApi } from '@common/reducers/backend-api/backend-api';
import { refreshSessions } from './refresh-sessions';

/**
 * Creating and (re)starting a remote (always-on) WhatsApp connection — one implementation for
 * every surface that offers it (/connections and /onboarding), so both run identical code.
 */
export function useRemoteConnection() {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUserObject);
  const [createSession, { isLoading: isCreating }] = backendApi.useCreateSessionMutation();
  const [createK8sEngine] = backendApi.useCreateK8sEngineMutation();

  /**
   * Creates the session record, loads it into the store, hands its id to `onSessionReady` (so the
   * caller can show the Prepare → Scan flow straight away), then starts the engine.
   * Resolves with the new session id, or undefined if anything failed (already toasted).
   */
  // Sessions whose engine start is in flight (from create OR ensureRunning) — so the session a
  // create just loaded into the store, still without a deploymentName, is never started twice.
  const startingRef = useRef<Set<string>>(new Set());

  const createRemote = useCallback(async (onSessionReady?: (sessionId: string) => void) => {
    if (!user?._id) return undefined;
    let sessionId: string | undefined;
    try {
      const sessionResult = await createSession({
        owner: user._id,
        engineType: 'session-pod',
        gatewayType: 'wa-js-gcp',
        status: 'initializing',
        active: true,
        k8sNamespace: 'blueticks-adhock-gateways',
      }).unwrap();
      sessionId = sessionResult._id as string;
      startingRef.current.add(sessionId);
      // The connect flow closes itself if its engine is missing from the store, so load first.
      await dispatch(refreshSessions());
      onSessionReady?.(sessionResult._id);
      await createK8sEngine({ sessionId: sessionResult._id }).unwrap();
      // Awaited, so the guard below is only released once the store holds the started session.
      await dispatch(refreshSessions());
      return sessionResult._id as string;
    } catch (error) {
      console.error(t('enginesManagement.createEngineError'), error);
      toast.error(t('enginesManagement.createEngineError'));
      return undefined;
    } finally {
      if (sessionId) startingRef.current.delete(sessionId);
    }
  }, [user?._id, createSession, createK8sEngine, dispatch, t]);

  /**
   * A decommissioned gateway (WhatsApp unlinked it → deployment + pod torn down, session kept for
   * re-link) has nothing running that could ever produce a QR. Start an engine for it; a session
   * that still has a deployment is left alone.
   */
  const ensureRunning = useCallback((session: { _id: string; deploymentName?: string | null } | undefined) => {
    if (!session || session.deploymentName || startingRef.current.has(session._id)) return;
    startingRef.current.add(session._id);
    createK8sEngine({ sessionId: session._id }).unwrap()
      .then(() => dispatch(refreshSessions()))
      .catch((error) => {
        console.error(t('enginesManagement.createEngineError'), error);
        toast.error(t('enginesManagement.createEngineError'));
      })
      .finally(() => startingRef.current.delete(session._id));
  }, [createK8sEngine, dispatch, t]);

  return { createRemote, ensureRunning, isCreating };
}
